<script lang="ts">
    import dayjs from 'dayjs';
    import {
        VpArrowDownIcon,
        VpChevronRightIcon,
        VpArrowUpIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createPagination } from '@melt-ui/svelte';
    import { fade } from 'svelte/transition';

    import cn from '$common/utils/cn';
    import { formatCurrency } from '$saga/utils/price';
    import { formatTourDate, formatTourDate2 } from '$saga/utils/date';
    import {
        getCurrentOrNextAvailableMonth,
        isDateRemainingStockAvailable,
        isDateRemainingStockLast,
    } from '$saga/views/Tour/VisibleDates/utils';
    import Button from '$saga/components/Button.svelte';
    import type {
        Dict,
        MinPriceMonth,
        VisibleDate,
    } from '$saga/views/Tour/VisibleDates/types';
    import MinPricePerMonthsList from '$saga/views/Tour/VisibleDates/MinPricePerMonthsList.svelte';
    import AgeRangePricesDrawer from '$saga/views/Tour/VisibleDates/AgeRangePricesDrawer.svelte';
    import Tooltip from '$saga/components/Tooltip.svelte';
    import UnavailableTour from '$saga/views/Tour/VisibleDates/UnavailableTour.svelte';
    import {
        datesInfos,
        modalStep,
        selectedPrice,
    } from '$saga/views/Tour/store';
    import { isModalOpen } from '$saga/views/Tour/store';

    export let tourDatesInfo: string,
        visibleDates: VisibleDate[],
        dict: Dict,
        searchUrl: string,
        isBookable: boolean,
        cheapestMonthlyPrices;

    let minPricePerMonths: MinPriceMonth[] = Object.keys(
        cheapestMonthlyPrices,
    ).map((key) => ({
        // key is the date
        date: dayjs(key),
        price: cheapestMonthlyPrices[key]?.amount,
        quantile: cheapestMonthlyPrices[key]?.quantile,
    }));
    let isShowingMore = false;
    let currentActiveMonth = getCurrentOrNextAvailableMonth(
        minPricePerMonths,
        visibleDates,
    );
    let filteredDatesByMonth: VisibleDate[] = visibleDates.filter(
        (visibleDate) =>
            dayjs(visibleDate.date1.date).isSame(currentActiveMonth, 'month'),
    );

    $: {
        filteredDatesByMonth = visibleDates.filter((visibleDate) =>
            dayjs(visibleDate.date1.date).isSame(currentActiveMonth, 'month'),
        );
        // reset showmore button at every currentActiveMonth change
        isShowingMore = false;
    }

    const {
        elements: { root, prevButton, nextButton },
        states: { range, totalPages, page },
    } = createPagination({
        count: minPricePerMonths.length - 1,
        perPage: 5,
    });

    const getDateTooltip = (date: VisibleDate) => {
        if (date.isFull) return dict.dates.full;
        else if (date.isGuaranteed) {
            return dict.status.confirm;
        }
        return dict.status.available.replace(
            '%minPax%',
            date.countPaxNeededToConfirm.toString(),
        );
    };

    const handleSelectedDate = (event: Event, date: VisibleDate) => {
        const target = (
            event.currentTarget as HTMLButtonElement
        )?.querySelector('input[name="startDateTable"]');
        // avoid event to be retriggered by the input
        if (
            target &&
            (event.target as HTMLButtonElement)?.tagName !== 'INPUT'
        ) {
            (target as HTMLInputElement).click();
        }
        //send selected date to store
        $selectedPrice = {
            originalPrice: date.hasRebate ? date.price : null,
            rebateAmount:
                date.hasRebate && date.rebateAmount ? date.rebateAmount : null,
            actualPrice:
                date.hasRebate && date.rebatedPrice
                    ? date.rebatedPrice
                    : date.price,
        };

        $datesInfos = {
            price: date.hasRebate ? date.rebatedPrice : date.price,
            remainingStock: date?.remaining_stock ?? '',
            statusName: date?.statusName ?? '',
            selectedDate: dayjs(date.date1.date).format('YYYY-MM-DD'),
        };
        $modalStep = 2;
        $isModalOpen = true;
    };
</script>

<div>
    {#if visibleDates.length > 0}
        <h2 class="pb-m text-[32px] font-semibold text-content-neutral">
            {dict.dates.title}
        </h2>
        <div class="w-full bg-white p-m text-content-neutral">
            <div class="mb-2xl flex flex-col gap-m">
                <h3 class="text-[22px] font-semibold">{dict.dates.start}</h3>
                <span class="text-center font-semibold">
                    {dict.dates.months_select}
                </span>
                <MinPricePerMonthsList
                    {root}
                    {prevButton}
                    {nextButton}
                    {page}
                    {range}
                    bind:currentActiveMonth
                    {totalPages}
                    {minPricePerMonths}
                    {visibleDates}
                />
            </div>

            <!--desktop -->
            <div class="hidden w-full desktop:block">
                {#each filteredDatesByMonth as date, index (date.id)}
                    <button
                        on:click={(event) =>
                            !date.isFull && handleSelectedDate(event, date)}
                        class={cn(
                            'mb-xs flex w-full items-center border border-solid border-border-quiet p-xs',
                            !date.isFull && 'cursor-pointer',
                            !isShowingMore && index > 4 && 'hidden',
                            !dayjs(date.date1.date).isSame(
                                currentActiveMonth,
                                'month',
                            ) && 'hidden',
                        )}
                    >
                        <span
                            class={cn('flex', date.isFull && 'opacity-[0.38]')}
                        >
                            <input
                                name="startDateTable"
                                type="radio"
                                value={dayjs(date.date1.date).format(
                                    'YYYY-MM-DD',
                                )}
                                disabled={date.isFull}
                            />
                        </span>
                        <span class="w-full">
                            <span class={cn(date.isFull && 'opacity-[0.38]')}>
                                du <span class="font-semibold">
                                    {formatTourDate(date.date1.date)}
                                </span>
                                au
                                <span class="font-semibold">
                                    {formatTourDate2(date.date2.date)}
                                </span>
                            </span>
                        </span>

                        <span class="w-full">
                            {#if date.isFull}
                                <span class="flex flex-col gap-2xs">
                                    <span
                                        class="w-fit font-semibold text-content-brand"
                                    >
                                        <Tooltip
                                            tooltipContentClass="bg-content-quiet text-white"
                                            tooltipContent={getDateTooltip(
                                                date,
                                            )}
                                            tooltipLabel={date.statusName}
                                            hasArrow={true}
                                        />
                                    </span>
                                </span>
                            {/if}
                            {#if date.isGuaranteed}
                                <span class="flex flex-col gap-2xs">
                                    <span
                                        class="font-semibold text-content-brand"
                                    >
                                        <Tooltip
                                            tooltipContentClass="bg-content-quiet text-white"
                                            tooltipContent={getDateTooltip(
                                                date,
                                            )}
                                            tooltipLabel={date.statusName}
                                            hasArrow={true}
                                        />
                                    </span>
                                    {#if isDateRemainingStockAvailable(date)}
                                        <span class="text-left text-xs">
                                            {date.remaining_stock} place{!isDateRemainingStockLast(
                                                date,
                                            )
                                                ? 's'
                                                : ''} disponible{!isDateRemainingStockLast(
                                                date,
                                            )
                                                ? 's'
                                                : ''}
                                            {#if date.paxSoldCount > 0}
                                                <span
                                                    >- {date.paxSoldCount} inscrits</span
                                                >
                                            {/if}
                                        </span>
                                    {/if}
                                </span>
                            {/if}
                            {#if date.isAvailable}
                                <span class="flex flex-col gap-2xs">
                                    <span
                                        class="font-semibold text-content-brand"
                                    >
                                        <Tooltip
                                            tooltipContentClass="bg-content-quiet text-white"
                                            tooltipContent={getDateTooltip(
                                                date,
                                            )}
                                            tooltipLabel={date.statusName}
                                            hasArrow={true}
                                        />
                                    </span>
                                    {#if isDateRemainingStockAvailable(date)}
                                        <span class="text-left text-xs">
                                            {date.remaining_stock} place{!isDateRemainingStockLast(
                                                date,
                                            )
                                                ? 's'
                                                : ''} disponible{!isDateRemainingStockLast(
                                                date,
                                            )
                                                ? 's'
                                                : ''}
                                            {#if date.paxSoldCount > 0}
                                                <span
                                                    >- {date.paxSoldCount} inscrits</span
                                                >
                                            {/if}
                                        </span>
                                    {/if}
                                </span>
                            {/if}
                        </span>

                        <span class="w-full">
                            <span
                                class={cn(
                                    'flex items-center justify-end gap-xs',
                                    date.isFull && 'opacity-[0.38]',
                                )}
                            >
                                {#if !isBookable}
                                    <span class="text-xs">À partir de</span>
                                {/if}
                                {#if date.hasRebate}
                                    <span
                                        class="travel-tour-dates__price font-semibold line-through"
                                    >
                                        {formatCurrency(date.price)}
                                    </span>
                                    <span
                                        class="bg-container-commercial-catchy p-2xs text-xs"
                                    >
                                        {date.rebateAmount}
                                    </span>
                                    <span
                                        class="travel-tour-dates__price text-[20px] font-semibold"
                                        data-date={dayjs(
                                            date.date1.date,
                                        ).format('YYYY-MM-DD')}
                                    >
                                        {formatCurrency(date.rebatedPrice ?? 0)}
                                    </span>
                                {:else}
                                    <span
                                        class="travel-tour-dates__price text-xl font-semibold"
                                        data-date={dayjs(
                                            date.date1.date,
                                        ).format('YYYY-MM-DD')}
                                    >
                                        {formatCurrency(date.price)}
                                    </span>
                                {/if}
                                {#if date.ageRangePrices.length > 1}
                                    <AgeRangePricesDrawer
                                        disabled={date.isFull}
                                        ageRangePrices={date.ageRangePrices}
                                    />
                                {/if}
                            </span>
                        </span>
                    </button>
                {:else}
                    <div in:fade class="flex flex-col gap-xs mt-xs text-center">
                        <span class="font-semibold">
                            {dict.dates.empty.title}
                        </span>
                        <span class="text-sm">
                            {dict.dates.empty.description}
                        </span>
                    </div>
                {/each}
            </div>

            <!--mobile -->
            <div
                class="mt-m border-t border-solid border-border-quiet desktop:hidden"
            >
                {#each filteredDatesByMonth as date, index (date.id)}
                    <button
                        in:fade
                        class={cn(
                            'flex w-full border-b border-solid border-border-quiet py-xs',
                            !isShowingMore && index > 4 && 'hidden',
                        )}
                        disabled={date.isFull}
                        on:click={(event) =>
                            !date.isFull && handleSelectedDate(event, date)}
                    >
                        <span class="flex w-full flex-col gap-xs text-left">
                            <span class={cn(date.isFull && 'opacity-[0.38]')}>
                                Du <span class="font-semibold">
                                    {formatTourDate(date.date1.date)}
                                </span>
                                au
                                <span class="font-semibold">
                                    {formatTourDate2(date.date2.date)}
                                </span>
                            </span>
                            {#if date.isFull}
                                <div class="flex flex-col gap-2xs">
                                    <span class="font-semibold">Complet</span>
                                    <span
                                        class="text-sm text-content-brand opacity-[0.38]"
                                    >
                                        Indisponible
                                    </span>
                                </div>
                            {:else}
                                <div class="flex flex-col gap-2xs">
                                    <span
                                        class="font-semibold text-content-brand"
                                    >
                                        {date.statusName}
                                    </span>
                                    {#if isDateRemainingStockAvailable(date)}
                                        <span
                                            class="text-sm text-content-brand"
                                        >
                                            {date.remaining_stock} place{!isDateRemainingStockLast(
                                                date,
                                            )
                                                ? 's'
                                                : ''} disponible{!isDateRemainingStockLast(
                                                date,
                                            )
                                                ? 's'
                                                : ''}
                                            {#if date.paxSoldCount > 0}
                                                <span
                                                    >- {date.paxSoldCount} inscrits</span
                                                >
                                            {/if}
                                        </span>
                                    {/if}
                                </div>
                            {/if}
                            <span
                                class={cn(
                                    'flex items-center gap-xs',
                                    date.isFull && 'opacity-[0.38]',
                                )}
                            >
                                {#if !isBookable}
                                    <span class="text-xs">À partir de</span>
                                {/if}
                                {#if date.hasRebate}
                                    <span class="line-through">
                                        {formatCurrency(date.price)}
                                    </span>
                                    <span
                                        class="w-fit bg-container-commercial-catchy p-2xs text-xs"
                                    >
                                        {date.rebateAmount}
                                    </span>

                                    <span class="pl-xs font-semibold">
                                        {formatCurrency(date.rebatedPrice ?? 0)}
                                    </span>
                                {:else}
                                    <span class={cn('font-semibold')}>
                                        {formatCurrency(date.price)}
                                    </span>
                                {/if}
                            </span>
                        </span>
                        <span class="m-auto h-full">
                            <VpChevronRightIcon
                                class={cn(
                                    'size-l text-content-neutral',
                                    date.isFull &&
                                        'text-content-quiet opacity-[0.38]',
                                )}
                            />
                        </span>
                    </button>
                {:else}
                    <div in:fade class="mt-xs text-center flex flex-col gap-xs">
                        <span class="font-semibold">
                            {dict.dates.empty.title}
                        </span>
                        <span class="text-sm">
                            {dict.dates.empty.description}
                        </span>
                    </div>
                {/each}
            </div>

            {#if filteredDatesByMonth.length > 5}
                <div class="my-m flex items-center justify-center">
                    <Button
                        intent="tertiary"
                        size="small"
                        endIcon={!isShowingMore
                            ? VpArrowDownIcon
                            : VpArrowUpIcon}
                        on:click={() => (isShowingMore = !isShowingMore)}
                    >
                        {#if !isShowingMore}
                            {dict.dates.showmore}
                        {:else}
                            {dict.dates.showless}
                        {/if}
                    </Button>
                </div>
            {/if}
        </div>
    {:else}
        <UnavailableTour {dict} {searchUrl} />
    {/if}
    {#if tourDatesInfo}
        <div class="info">
            <h3 class="heading">{dict.dates.dates_info}</h3>
            <div class="content is-rt">
                <!--  eslint-disable-next-line svelte/no-at-html-tags -->
                {@html tourDatesInfo}
            </div>
        </div>
    {/if}
</div>
